/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 *
 */
import styled from 'styled-components';
import StyledProps from '../../../common/theme/AppThemeInterface';
import { TAIL_DETAILS_PANEL_WIDTH } from '../../TailDetailsPanel/TailDetailsPanel';

export const CHART_WRAPPER_HEIGHT_PX = 185;
export const CHART_WRAPPER_SMALL_HEIGHT_PX = 149;
export const CHART_WRAPPER_LARGE_HEIGHT_PX = 200;

export enum CHART_HEIGHT {
  DEFAULT = 'default',
  SMALL = 'small',
  LARGE = 'large',
}

export const StyledChartContainerBuffer = styled.div`
  background-color: ${({ theme }: ChartsContainerWrapperProps) => theme.secondaryBackgroundColor};
  height: ${CHART_WRAPPER_HEIGHT_PX}px;
`;

interface ChartsContainerWrapperProps extends StyledProps {
  printMode: boolean;
  mapHeight: number;
  leftMargin: number;
}

/**
 * Wraps the chart container with the given CSS. Note that the highcharts theme is overwritten here.
 * Highcharts default theme used in this application is the Dark Unica theme:
 *  https://www.highcharts.com/docs/chart-design-and-style/custom-themes-in-styled-mode
 */
const ChartsContainerWrapper = styled.div`
  background-color: ${({ theme }: ChartsContainerWrapperProps) => theme.secondaryBackgroundColor};
  width: ${({ printMode, leftMargin = TAIL_DETAILS_PANEL_WIDTH }: ChartsContainerWrapperProps) =>
    printMode ? '100%' : `calc(100% - ${leftMargin}px - 1px)`};

  height: ${({ printMode }: ChartsContainerWrapperProps) => (printMode ? 'auto' : '100vh')};
  top: ${({ mapHeight }: ChartsContainerWrapperProps) => `${mapHeight}px`};
  position: absolute;
  left: ${({ printMode, leftMargin = TAIL_DETAILS_PANEL_WIDTH }: ChartsContainerWrapperProps) =>
    printMode ? 'auto' : `${leftMargin}px`};

  @media print {
    background-color: white;
  }

  .highcharts-label {
    visibility: visible;
  }
  .highcharts-legend {
    z-index: 500;

    .highcharts-point {
      display: none;
    }

    .highcharts-graph {
      display: none;
    }

    .highcharts-legend-navigation {
      fill: ${({ theme }: ChartsContainerWrapperProps) => theme.primaryTextColor};
    }

    .highcharts-legend-title {
      font-size: 12px;
      line-height: 16px;
      font-weight: bold;
      font-family: ${({ theme }: ChartsContainerWrapperProps) => theme.appFontFamily};
      color: ${({ theme }: ChartsContainerWrapperProps) => theme.primaryTextColor};
      margin: -3px;
    }
  }
  .ftv__charts__droppable {
    overflow-y: visible;
    padding: 16px 16px 8px 16px;
    background-color: ${({ theme }: ChartsContainerWrapperProps) => theme.secondaryBackgroundColor};

    @media print {
      background: white;
    }
  }

  .ftv__charts__draggable {
    height: ${CHART_WRAPPER_HEIGHT_PX + 1}px;
    break-inside: avoid;
  }

  .ftv__charts__draggable--small {
    height: ${CHART_WRAPPER_SMALL_HEIGHT_PX + 1}px;
    break-inside: avoid;
  }

  .ftv__charts__draggable--large {
    height: ${CHART_WRAPPER_LARGE_HEIGHT_PX + 1}px;
    break-inside: avoid;
  }

  .ftv__charts__draggable--dragIndicator {
    position: relative;
    float: right;
    right: 12px;
    cursor: move;
    cursor: grab;
    z-index: 1;
  }

  .ftv__charts__loading--container {
    background-color: ${({ theme }: ChartsContainerWrapperProps) => theme.primaryBackgroundColor};
    width: 100%;
    height: 100%;
    position: absolute;

    @media print {
      position: relative !important;
    }

    text-align: center;

    .error__message__container {
      color: ${({ theme }: StyledProps) => theme.primaryTextColor};
      position: relative;
      top: 50px;

      .try__again__button {
        z-index: 5;
        color: ${({ theme }: StyledProps) => theme.buttonSelectedColor};
      }
    }
  }

  .ftv__charts__loading {
    position: relative;
    top: 60px;
  }

  .ftv__charts__header {
    margin-left: 12px;
    padding-top: 12px;
    margin-bottom: 14px;

    @media print {
      margin-left: 0px;
    }
  }

  .ftv__charts__header--small {
    margin-left: 12px;
    padding-top: 12px;
    margin-bottom: 8px;

    @media print {
      margin-left: 0px;
    }
  }

  .ftv__charts__header--large {
    margin-left: 12px;
    padding-top: 12px;
    margin-bottom: 14px;

    @media print {
      margin-left: 0px;
    }
  }

  .ftv__charts__title {
    font-family: ${({ theme }: ChartsContainerWrapperProps) => theme.appFontFamily};
    font-size: 16px;
    line-height: 22px;

    @media print {
      color: black;
    }
  }

  .ftv__charts__time {
    font-size: 12px;
    color: #9fafbc;
  }

  /* Highcharts Theme */

  .highcharts-container {
    font-family: ${({ theme }: ChartsContainerWrapperProps) => theme.appFontFamily};
    font-size: 14px;
  }

  .highcharts-background {
    fill: transparent;
  }

  .ftv__highcharts__chart--crosshair {
    cursor: ew-resize;
  }

  .highcharts-plot-background {
    cursor: ew-resize;
    fill: transparent;
  }

  .highcharts-tooltip-box {
    fill: #141d24;
    fill-opacity: 1;
    stroke: none;
  }
  .highcharts-tooltip {
    font-size: 12px;
    > span {
      top: 0 !important;
      left: 0 !important;
      padding: 0 !important;
    }
  }

  .highcharts-tooltip .highcharts-header {
    font-size: 12px;
    fill: rgb(0, 0, 0);
  }

  .highcharts-root {
    position: relative;
    z-index: 1;
  }

  .highcharts-annotation {
    z-index: 0;
  }

  .highcharts-axis-labels {
    font-size: 12px;
  }

  /* Timeline */

  .ftv__charts--timeline .highcharts-point {
    stroke: ${({ theme }: ChartsContainerWrapperProps) => theme.primaryBackgroundColor};
    stroke-width: 2px;
  }

  .ftv__charts--timeline .highcharts-data-label > text {
    fill: ${({ theme }: ChartsContainerWrapperProps) => theme.primaryTextColor};
    stroke: none;
  }

  .ftv__charts--timeline .highcharts-data-label {
    font-size: 16px;
  }

  .ftv__timeline--tooltip {
    pointer-events: all;
  }

  .ftv__charts__timeline--marker .highcharts-point {
    fill: #ffaf07;
  }

  .ftv__charts__timeline--marker .highcharts-point .spot-beam-icon {
    visibility: hidden;
  }

  .ftv__charts__timeline--marker .highcharts-point-hover .spot-beam-icon {
    visibility: visible;
  }

  .ftv__charts--timeline .highcharts-halo {
    fill: none;
    fill-opacity: 1;
    stroke: #ffffff;
    stroke-width: 2px;
  }

  .ftv__charts--timeline .highcharts-grid-line {
    stroke: none;
  }

  .ftv__charts--timeline .highcharts-series-inactive,
  .ftv__charts--timeline .highcharts-scatter-series {
    opacity: 1 !important;
  }

  .highcharts-annotation-label-box {
    display: none;
  }

  .highcharts-annotation-shapes > rect {
    fill: #ffaf07;
  }

  .ftv__charts__timeline--connected {
    fill: #5ea215;
    stroke: transparent;
  }

  .ftv__charts__timeline--standby {
    fill: #c5f042;
    stroke: transparent;
  }

  .ftv__charts__timeline--disconnected {
    fill: #c3cdd5;
    stroke: transparent;
  }

  .ftv__charts__timeline--marker {
    fill: #ffaf07;
    stroke: transparent;
  }

  @media print {
    .ftv__charts__draggable {
      break-inside: avoid;
    }

    .ftv__charts__draggable--small {
      break-inside: avoid;
    }

    .ftv__charts__draggable--large {
      break-inside: avoid;
    }
  }
`;

export default ChartsContainerWrapper;
